import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { CatalogMetric, ClientDetail, ModuleKey, MonthlyEntry } from '@pixerito/api-client';
import { ErrorNotice, Icon, Loading } from '../../components/ui';
import { useAsync } from '../../lib/hooks';
import { parseInput } from '../../lib/parse';
import { currentPeriod, formatDateTime, formatValue, monthLong, monthName, percentChange, shiftPeriod } from '../../lib/format';

function toInput(value: number | null | undefined, decimals: number) {
  if (value === null || value === undefined) return '';
  return decimals === 0 ? String(Math.round(value)) : value.toLocaleString('pt-BR', { maximumFractionDigits: decimals });
}

export function MonthlyEntryForm({ client, onSaved }: { client: ClientDetail; onSaved: (msg: string) => void }) {
  const catalog = useAsync(() => api.catalog(), []);
  const metricModules = client.modules.filter((m) => m.enabled && ['social', 'site', 'gmb'].includes(m.type));
  const [module, setModule] = useState<ModuleKey | undefined>(metricModules[0]?.type);
  const [period, setPeriod] = useState(shiftPeriod(currentPeriod(), -1));

  const months = useMemo(() => Array.from({ length: 18 }, (_, i) => shiftPeriod(currentPeriod(), -i)), []);

  if (metricModules.length === 0) {
    return (
      <div className="empty">
        <p className="t-h3">Nenhum módulo com métricas</p>
        <p className="muted t-small">
          Ative Redes Sociais, Site ou Google Meu Negócio para lançar números mensais. Entregáveis e vídeos têm abas próprias.
        </p>
      </div>
    );
  }
  if (catalog.loading && !catalog.data) return <Loading />;
  if (catalog.error) return <ErrorNotice message={catalog.error} onRetry={catalog.reload} />;

  const def = catalog.data?.find((m) => m.type === module);

  return (
    <div>
      <div className="entry-toolbar">
        <div className="field">
          <label htmlFor="entry-month">Mês de referência</label>
          <select id="entry-month" className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {months.map((p) => (
              <option key={p} value={p}>
                {monthName(p)} {p.slice(0, 4)}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 auto' }}>
          <span className="field__label">Módulo</span>
          <div className="tabs" role="tablist" aria-label="Módulo">
            {metricModules.map((m) => (
              <button key={m.type} role="tab" aria-selected={m.type === module} onClick={() => setModule(m.type)}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {def && module && (
        <EntryEditor
          key={`${module}-${period}`}
          clientId={client.id}
          module={module}
          label={def.label}
          metrics={def.metrics}
          period={period}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function EntryEditor({
  clientId,
  module,
  label,
  metrics,
  period,
  onSaved,
}: {
  clientId: string;
  module: ModuleKey;
  label: string;
  metrics: CatalogMetric[];
  period: string;
  onSaved: (msg: string) => void;
}) {
  const entry = useAsync(() => api.monthly(clientId, module, period), [clientId, module, period]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const inputs = metrics.filter((m) => !m.derived);
  useEffect(() => {
    if (entry.data) setDraft(Object.fromEntries(inputs.map((m) => [m.key, toInput(entry.data!.values[m.key], m.decimals)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.data]);

  if (entry.loading && !entry.data) return <Loading />;
  if (entry.error) return <ErrorNotice message={entry.error} onRetry={entry.reload} />;
  const data = entry.data as MonthlyEntry;

  const parsed: Record<string, number | null | 'invalid'> = Object.fromEntries(
    inputs.map((m) => [m.key, parseInput(draft[m.key] ?? '', m.decimals)]),
  );
  const dirty = inputs.some((m) => (parsed[m.key] === 'invalid' ? true : parsed[m.key]) !== (data.values[m.key] ?? null));
  const filled = inputs.filter((m) => parsed[m.key] !== null).length;

  const valueOf = (m: CatalogMetric): number | null => {
    if (!m.derived) {
      const v = parsed[m.key];
      return typeof v === 'number' ? v : null;
    }
    const parts = sumMembers(m)
      .map((k) => parsed[k])
      .filter((v): v is number => typeof v === 'number');
    return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
  };
  const prevOf = (m: CatalogMetric): number | null => {
    if (!m.derived) return data.previousValues[m.key] ?? null;
    const parts = sumMembers(m)
      .map((k) => data.previousValues[k])
      .filter((v): v is number => v !== null && v !== undefined);
    return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
  };

  const ungrouped = metrics.filter((m) => !m.group && !m.derived);
  const groups = [...new Set(metrics.filter((m) => m.group).map((m) => m.group!))];

  async function save() {
    setMessage(null);
    const local: Record<string, string> = {};
    const values: Record<string, number | null> = {};
    for (const m of inputs) {
      const v = parsed[m.key];
      if (v === 'invalid') local[m.key] = 'Use só números, ex.: 1250 ou 4,8.';
      else if (v !== null && v < 0) local[m.key] = 'Use um número maior ou igual a zero.';
      else if (v !== null && v > m.max) local[m.key] = `O máximo é ${m.max.toLocaleString('pt-BR')}.`;
      else values[m.key] = v;
    }
    setErrors(local);
    if (Object.keys(local).length) {
      setMessage('Confira os campos destacados.');
      return;
    }

    setBusy(true);
    try {
      await api.saveMonthly(clientId, module, period, values);
      onSaved(`${label}: ${monthLong(period)} salvo`);
      entry.reload();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setMessage(err.message);
      } else setMessage('Não foi possível salvar. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  const row = (m: CatalogMetric) => {
    const cur = valueOf(m);
    const prev = prevOf(m);
    const change =
      cur !== null && m.unit === 'count'
        ? percentChange(cur, prev)
        : cur !== null && prev !== null
          ? Math.round((cur - prev) * 10) / 10
          : null;
    const id = `m-${m.key}`;
    return (
      <div key={m.key} className={`entry-row ${m.derived ? 'entry-row--derived' : ''}`}>
        <div className="entry-row__label">
          <label htmlFor={id} style={{ fontWeight: 500, fontSize: 14 }}>
            {m.label}
            {m.unit === 'percent' ? ' (%)' : ''}
          </label>
          {errors[m.key] ? (
            <span className="field__error">{errors[m.key]}</span>
          ) : (
            <span className="t-caption">{m.derived ? 'Soma automática' : (m.hint ?? ' ')}</span>
          )}
        </div>
        {m.derived ? (
          <input
            id={id}
            className="input input--num"
            value={cur === null ? '' : formatValue(cur, m.unit)}
            readOnly
            tabIndex={-1}
            aria-readonly="true"
          />
        ) : (
          <input
            id={id}
            className="input input--num"
            inputMode={m.decimals ? 'decimal' : 'numeric'}
            autoComplete="off"
            value={draft[m.key] ?? ''}
            aria-invalid={!!errors[m.key]}
            placeholder={prev !== null ? toInput(prev, m.decimals) : '0'}
            onChange={(e) => setDraft({ ...draft, [m.key]: e.target.value })}
          />
        )}
        <div className="entry-row__prev">
          <span className="t-caption">
            {monthName(shiftPeriod(period, -1))}: {prev === null ? '—' : formatValue(prev, m.unit)}
          </span>
          {change !== null && prev !== null && (
            <span className={`delta ${change > 0 ? 'delta--good' : change < 0 ? 'delta--bad' : 'delta--flat'}`}>
              {change === 0
                ? '= igual'
                : `${change > 0 ? '▲ +' : '▼ −'}${Math.abs(change).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}${m.unit === 'count' ? '%' : m.unit === 'rating' ? ' pt' : ' p.p.'}`}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h2 className="t-h3">
            {label} · {monthLong(period)}
          </h2>
          <p className="t-caption">
            {data.updatedAt
              ? `Último salvamento em ${formatDateTime(data.updatedAt)}${data.source === 'api' ? ' (via API)' : ''}.`
              : 'Ainda sem números neste mês.'}{' '}
            Deixe em branco o que não tiver.
          </p>
        </div>
      </div>

      {message && (
        <div className="notice notice--error" role="alert" style={{ marginBottom: 16 }}>
          {message}
        </div>
      )}

      {ungrouped.length > 0 && <div className="entry-group">{ungrouped.map(row)}</div>}
      {groups.map((g) => {
        const items = metrics.filter((m) => m.group === g);
        const derived = metrics.find((m) => m.derived && sumMembers(m).every((k) => items.some((i) => i.key === k)));
        return (
          <div key={g} className="entry-group">
            <p className="label" style={{ marginTop: 8 }}>
              {g}
            </p>
            {items.map(row)}
            {derived && row(derived)}
          </div>
        );
      })}

      <div className="save-bar">
        <span className="t-small muted">
          {dirty ? (
            <>
              <span className="hl">●</span> Alterações não salvas ·{' '}
            </>
          ) : null}
          {filled} de {inputs.length} preenchidos
        </span>
        <button className="btn btn--action" onClick={save} disabled={busy || !dirty}>
          {busy ? (
            'Salvando…'
          ) : (
            <>
              <Icon name="check" size={16} />
              Salvar {monthName(period).toLowerCase()}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/** As chaves somadas por uma métrica derivada (ex.: conversões = formulário + WhatsApp + cliques). */
function sumMembers(derived: CatalogMetric): string[] {
  return derived.sumOf ?? [];
}
